import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";

export const Route = createFileRoute("/_authed/prompts/")({
  component: PromptsList,
});

function PromptsList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["admin", "prompts"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/prompts");
      if (error) throw new Error("failed to load prompts");
      return data!;
    },
  });

  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.DELETE("/api/v1/admin/prompts/{prompt_id}", {
        params: { path: { prompt_id: id } },
      });
      if (error) throw new Error("delete failed");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "prompts"] });
      setConfirmDelete(null);
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">Prompts</h1>
          <p className="text-sm text-zinc-500">
            DB-stored Mustache prompts. Edit metadata + manage version
            lifecycle (draft → active → retired) per prompt.
          </p>
        </div>
        <Button onClick={() => navigate({ to: "/prompts/new" })}>
          <Plus className="mr-1 size-4" />
          New prompt
        </Button>
      </div>

      <Card>
        <CardBody className="p-0">
          {q.isPending ? (
            <p className="p-4 text-sm text-zinc-500">Loading…</p>
          ) : q.isError ? (
            <p className="p-4 text-sm text-red-600">Failed to load prompts.</p>
          ) : q.data?.items.length === 0 ? (
            <p className="p-6 text-center text-sm text-zinc-500">
              No prompts yet.
            </p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Code</TH>
                  <TH>Name</TH>
                  <TH>Active version</TH>
                  <TH>LLM</TH>
                  <TH>Activated</TH>
                  <TH className="w-px text-right">Actions</TH>
                </TR>
              </THead>
              <TBody>
                {q.data!.items.map((p) => (
                  <TR key={p.id}>
                    <TD className="font-mono text-xs">
                      <Link
                        to="/prompts/$id"
                        params={{ id: p.id }}
                        className="text-zinc-900 underline"
                      >
                        {p.code}
                      </Link>
                    </TD>
                    <TD className="text-sm">{p.name}</TD>
                    <TD className="text-xs">
                      {p.active_version ? (
                        <span className="rounded bg-green-100 px-2 py-0.5 font-medium text-green-800">
                          v{p.active_version.version_number}
                        </span>
                      ) : (
                        <span className="text-zinc-400">— none —</span>
                      )}
                    </TD>
                    <TD className="text-xs text-zinc-600">
                      {p.active_version?.llm_code ?? "—"}
                    </TD>
                    <TD className="text-xs text-zinc-500">
                      {p.active_version?.activated_at
                        ? new Date(p.active_version.activated_at).toLocaleString(
                            undefined,
                            { dateStyle: "medium", timeStyle: "short" },
                          )
                        : "—"}
                    </TD>
                    <TD className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setConfirmDelete(p.id)}
                        aria-label="Delete prompt"
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>

      {confirmDelete && (
        <Dialog
          open
          onClose={() => setConfirmDelete(null)}
          title="Delete prompt?"
        >
          <p className="text-sm text-zinc-700">
            Soft-delete this prompt. Existing usage history is preserved; the
            code becomes available for re-use.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setConfirmDelete(null)}
              disabled={remove.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => remove.mutate(confirmDelete)}
              disabled={remove.isPending}
            >
              {remove.isPending ? "Deleting…" : "Delete"}
            </Button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
